/**
 * ShortcutHelpOverlay — modal listing all keyboard shortcuts.
 *
 * Triggered by pressing `?` key (when not in a text input).
 * Groups shortcuts into logical sections: Navigation, Task Actions,
 * Creation, and View Navigation.
 *
 * Uses the `shortcutLabel()` utility for cross-platform labels.
 *
 * @see §8.6 in task-detail-fullpost-plan.md
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/components/ui/dialog"
import { shortcutLabel } from "@/shared/utils/platform/shortcutLabel"

// ---------------------------------------------------------------------------
// Shortcut data
// ---------------------------------------------------------------------------

interface Shortcut {
  keys: string // canonical format for shortcutLabel()
  label: string // i18n key
}

interface ShortcutGroup {
  title: string // i18n key
  shortcuts: Shortcut[]
}

function useShortcutGroups(): ShortcutGroup[] {
  const { t } = useTranslation()
  return [
    {
      title: t("Navigation"),
      shortcuts: [
        { keys: "↑/↓", label: t("Move focus") },
        { keys: "Shift+↑/↓", label: t("Extend selection") },
        { keys: "Enter", label: t("Open task") },
        { keys: "Escape", label: t("Close / Deselect") },
        { keys: "Mod+A", label: t("Select all") },
        { keys: "Mod+F", label: t("Search") },
        { keys: "Mod+\\", label: t("Toggle sidebar") },
      ],
    },
    {
      title: t("Task actions"),
      shortcuts: [
        { keys: "Mod+K", label: t("Complete task") },
        { keys: "T", label: t("Toggle timer") },
        { keys: "Mod+S", label: t("Save & close") },
        { keys: "Mod+,", label: t("Open settings") },
      ],
    },
    {
      title: t("Task detail"),
      shortcuts: [
        { keys: "Mod+S", label: t("Schedule") },
        { keys: "Mod+L", label: t("Set deadline") },
        { keys: "Shift+Mod+M", label: t("Move to project") },
        { keys: "Mod+Backspace", label: t("Delete task") },
      ],
    },
    {
      title: t("Create"),
      shortcuts: [
        { keys: "Space", label: t("New task") },
        { keys: "N", label: t("Focus quick-add") },
        { keys: "P", label: t("New project") },
        { keys: "L", label: t("New list") },
        { keys: "A", label: t("New area") },
      ],
    },
    {
      title: t("Views"),
      shortcuts: [
        { keys: "1", label: t("Today") },
        { keys: "2", label: t("This Evening") },
        { keys: "3", label: t("Upcoming") },
        { keys: "4", label: t("Anyday") },
        { keys: "5", label: t("Someday") },
        { keys: "6", label: t("Logbook") },
        { keys: "I", label: t("Inbox") },
      ],
    },
  ]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShortcutHelpOverlay() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const groups = useShortcutGroups()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger on "?" (Shift+/ on most layouts)
      if (e.key !== "?") return
      // Don't trigger inside text inputs
      const target = e.target as HTMLElement
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.contentEditable === "true" ||
        target.closest(".ProseMirror") !== null ||
        target.closest("[data-tiptap-editor]") !== null
      )
        return
      e.preventDefault()
      setOpen((prev) => !prev)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Keyboard shortcuts")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("List of available keyboard shortcuts")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          {groups.map((group) => (
            <div key={group.title}>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.shortcuts.map((shortcut) => (
                  <div
                    key={shortcut.keys}
                    className="flex items-center justify-between py-1"
                  >
                    <span className="text-sm">{shortcut.label}</span>
                    <ShortcutBadge keys={shortcut.keys} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          {t("Press ? to toggle this overlay")}
        </p>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// ShortcutBadge — renders key combos as styled kbd elements
// ---------------------------------------------------------------------------

function ShortcutBadge({ keys }: { keys: string }) {
  // Handle compound labels like "↑/↓" or "Shift+↑/↓"
  const label = formatKeys(keys)
  return (
    <kbd className="inline-flex items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
      {label}
    </kbd>
  )
}

/** Format keys: if it contains Mod+, use shortcutLabel; else pass through. */
function formatKeys(keys: string): string {
  if (/mod\+/i.test(keys)) {
    return shortcutLabel(keys)
  }
  if (/shift\+/i.test(keys) && !/mod\+/i.test(keys)) {
    return shortcutLabel(keys)
  }
  return keys
}
